import React, { Component, Fragment } from 'react';
import propTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';

import { EDIT_STATUS_SAVED } from 'app/App/const';
import ComponentUsage from 'app/ComponentUsage/container';
import Publish from 'app/Publish/components/Publish';
import { visitComponent, visitPage } from 'app/Router/actions';
import { ClearButton, PrimaryButton } from 'ui/Components';

import styles from './PublishComponent.css';

const liveDCMessage = 'Changes made to currently live dynamic component cannot be scheduled.';

/**
 * This component is a redux connected component.
 * This component creates a button on UI and is the starting point of component publish event.
 * It shows the modal to setup publsh component
 * and eventually publishes the component as per the user inputs.
 * @param {object} props - the props passed to this component
 * @returns {React.Component} the PublishComponent component
 */
export default class PublishComponent extends Component {
  static propTypes = {
    clearComponentUsage: propTypes.func.isRequired,
    component: propTypes.shape(),
    editStatus: propTypes.string.isRequired,
    getComponentUsage: propTypes.func.isRequired,
    isReadonly: propTypes.bool,
    onPublish: propTypes.func.isRequired
  };

  static defaultProps = {
    component: {},
    isReadonly: true
  };

  constructor(props) {
    super(props);

    this.state = {
      isModalOpen: false
    };

    this.modalToggle = this.modalToggle.bind(this);
    this.onPublish = this.onPublish.bind(this);
  }

  /**
   * toggle the publish component modal and call the onPublish prop
   * @param {boolean} publishNow - publish true/false
   * @param {string} publishDate - UTC date time of publish
   * @param {boolean} isTestContent - if published for testing purposes only
   */
  onPublish(publishNow, publishDate, isTestContent = false) {
    this.modalToggle();
    this.props.onPublish(this.props.component, publishNow, publishDate, isTestContent);
  }

  /**
   * check whether the current component is dynamic
   * @returns {boolean} this.props.component.IsDynamic
   */
  get isDynamic() {
    return this.props.component.IsDynamic;
  }

  /**
   * toggle the publish component modal
   */
  modalToggle() {
    this.setState(ps => ({ isModalOpen: !ps.isModalOpen }));
  }

  /**
   * render buttons for non dynamic components
   * @returns {React.Component} modal footer buttons for master components
   */
  renderModalFooter() {
    const { clearComponentUsage, component, onPublish } = this.props;

    return (
      <Fragment>
        <PrimaryButton
          color="primary"
          onClick={() => {
            onPublish(component);
            this.modalToggle();
            clearComponentUsage();
          }}
          data-test-id="publishComponent-publishButton"
        >
          Publish
        </PrimaryButton>
        <ClearButton
          color="secondary"
          onClick={() => {
            this.modalToggle();
            clearComponentUsage();
          }}
          data-test-id="publishComponent-cancelButton"
        >
          Cancel
        </ClearButton>
      </Fragment>
    );
  }

  render() {
    const {
      editStatus,
      isReadonly,
      getComponentUsage,
      component,
      component: { ComponentRoute }
    } = this.props;
    const route = [ComponentRoute];

    return (
      <div className={styles.wrapper}>
        <div className={styles.pubupButton}>
          <PrimaryButton
            onClick={() => {
              this.modalToggle();
              getComponentUsage(component.Id);
            }}
            disabled={isReadonly || editStatus !== EDIT_STATUS_SAVED}
            data-test-id="publishComponent-pubupButton"
          >
            {this.isDynamic ? 'Publish' : 'Update'}
          </PrimaryButton>
        </div>
        <Modal
          isOpen={this.state.isModalOpen}
          toggle={this.modalToggle}
          data-test-id="publish-component-modal"
        >
          <ModalHeader data-test-id="publish-component-modal-header">
            {this.isDynamic ? 'Publish Component' : 'Update Component'}
          </ModalHeader>
          <ModalBody data-test-id="publish-component-modal-body">
            {this.isDynamic ? (
              <Publish
                type="component"
                onPublish={this.onPublish}
                showOnlyPublishNowButton={component.LiveNow}
                message={component.LiveNow ? liveDCMessage : ''}
                isTestContent={component.IsTestContent}
                routes={route}
              />
            ) : (
              <ComponentUsage
                unboundVisitComponent={visitComponent}
                unboundVisitPage={visitPage}
              />
            )}
          </ModalBody>
          <ModalFooter data-test-id="publish-component-modal-footer">
            {!this.isDynamic && this.renderModalFooter()}
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}
